import React from 'react';
import {Map} from 'immutable';
import {Plugins, Atom} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {reportBug} from '@packages/utilities/reporter';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {useWidgetConfig} from '@packages/shared/helpers/WidgetHelper';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';

const getDataSlider = (k: string, data: EvaluatedNodeData) =>
  getInPojo(data, ['slider', k]) ?? false;
const getDataAttr = (k: string, data: EvaluatedNodeData) =>
  getInPojo(data, ['attr', k]) ?? false;

// We use `data.slider` when it's available, but if it's not, we fallback onto
// `data.attr`. This whole block is temporary until we ship a migration /
// patch to move everything off of `data.slider` and onto `data.attr`. We have
// to do this in phases so that if we were to revert the migration the code
// still works. After we know the migration is successful (24 hours), we can
// remove this block and use `attributes` directly.

const getDataOrAttr = (
  dataName: string,
  attrName: string,
  defaultValue: number | string | boolean,
  data: EvaluatedNodeData
): string | number | boolean => {
  const sliderVal = getDataSlider(dataName, data);
  if (sliderVal !== undefined) return sliderVal as string | number | boolean;
  const attrVal = getDataAttr(attrName, data);
  if (attrVal !== undefined) return attrVal as string | number | boolean;

  reportBug(
    'PlatformSliderWrapperDataOrAttrError',
    'SliderWrapper has an undefined property on either data.slider or data.attr.',
    {
      data,
      dataName,
      team: 'platform',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-1010',
    }
  );

  return defaultValue;
};

const className = 'w-slider';

const SliderWrapper = Atom.createRender(
  ({attributes, data, classes, generateAttributes, renderChild}) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    const previewMode = usePreviewMode();
    const nativeNodeRef = useWidgetConfig(
      className,
      runtimeEnvironment,
      previewMode
    );

    const animation = getDataOrAttr(
      'animation',
      'data-animation',
      'slide',
      data
    );
    const autoMax = getDataOrAttr('autoMax', 'data-autoplay-limit', 0, data);
    const autoplay = getDataOrAttr('autoplay', 'data-autoplay', false, data);
    const duration = getDataOrAttr('duration', 'data-duration', 500, data);
    const delay = getDataOrAttr('delay', 'data-delay', 4000, data);
    const disableSwipe = getDataOrAttr(
      'disableSwipe',
      'data-disable-swipe',
      false,
      data
    );
    const easing = getDataOrAttr('easing', 'data-easing', 'ease', data);
    const hideArrows = getDataOrAttr(
      'hideArrows',
      'data-hide-arrows',
      false,
      data
    );
    const infinite = getDataOrAttr('infinite', 'data-infinite', true, data);
    const navSpacing = getDataOrAttr('navSpacing', 'data-nav-spacing', 3, data);

    const dataAttributes = Map({
      'data-animation': animation,
      'data-autoplay-limit': autoMax,
      'data-autoplay': autoplay,
      'data-delay': delay,
      'data-duration': duration,
      'data-disable-swipe': disableSwipe,
      'data-easing': easing,
      'data-hide-arrows': hideArrows,
      'data-infinite': infinite,
      'data-nav-spacing': navSpacing,
    });
    // @ts-expect-error - TS2769 - No overload matches this call.
    const newAttributes = attributes.merge(dataAttributes);

    const sliderContext = Map({
      invert: getDataSlider('navInvert', data),
      shadow: getDataSlider('navShadow', data),
      round: getDataSlider('navRound', data),
      numbers: getDataSlider('navNumbers', data),
    });

    const wrapperClasses = classes.push(className);
    return (
      <div
        ref={nativeNodeRef}
        {...generateAttributes(newAttributes, wrapperClasses)}
      >
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, sliderContext)
        )}
      </div>
    );
  }
);

const config = Atom.create({
  displayName: 'Slider',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Slider'), iconName('Slider')),
  },
  showEmpty: true,
  render: SliderWrapper,
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('Slider'), typeName('SliderWrapper'))
  ),
  showSettingsInDescendants: true,
});

export default config;
